import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import GiftIcon from '~/icons/components/GiftIcon';
import { promoPriceHiddenPromoTypes } from '~/shared/constants/products';
import useEMPPricing from '~/shared/utils/useEMPPricing';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { SkeletonLine, mobile } from '~/techstyle-shared/react-components';
import {
  FormattedMessage,
  Currency,
  useIntl,
} from '~/techstyle-shared/react-intl';

import { useGridProducts } from '../../context/GridDataContexts';

import MemberCreditCallout from './MemberCreditCallout';

const GiftIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  align-self: stretch;
`;

const PricingContainer = styled.div`
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const BoldPrice = styled(Currency)`
  color: ${({ theme }) => theme.colors.gr2ProductCardPricingStandard};
  font-family: inherit;
  font-size: 20px;
  ${mobile`
    font-size: 18px;
  `}
  font-style: normal;
  font-weight: 600;
  line-height: ${24 / 20};
`;

const StrikeThroughPrice = styled(Currency)`
  color: ${({ theme }) => theme.colors.gr2ProductCardPricingStrikethrough};
  font-family: inherit;
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: ${24 / 14};
  text-decoration-line: line-through;
`;

const FreeGiftText = styled.div`
  color: ${({ theme }) => theme.colors.gr2ProductCardPricingStandard};
  font-family: inherit;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: ${24 / 18};
`;

const PromoBox = styled.div`
  display: flex;
  padding: 8px 12px 10px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  background: ${({ theme }) => theme.colors.gr2PromoBoxBackground};
`;

const PromoText = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.pricingUiTestPromoText};
  text-overflow: ellipsis;
  font-family: inherit;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: ${16 / 12};
  text-transform: uppercase;
`;

const TopLabel = styled.div`
  align-self: stretch;
  color: ${({ theme }) => theme.colors.pricingUiTestBoldLabel};
  font-family: inherit;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: ${16 / 12};
  text-transform: capitalize;
`;

const PricingAndCredit = styled.div`
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const Pricing = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FreeGiftContainer = styled.div`
  display: flex;
  gap: 4px;
`;

const ProductCardPricing = ({ product }) => {
  const { formatMessage } = useIntl();
  const { isProductPricingLoading } = useGridProducts();
  const { isVip } = useMembership();
  const secondarySite = useSecondarySite();
  const { isTokenOnly, showMemberCredits } = useEMPPricing(product);

  if (!product?.pricing) {
    return null;
  }
  const { promoLabel: bogoPromoLabel } = product.pricing.bogoDetails;
  const {
    promoCode: discountPromoCode,
    promoLabel: discountPromoLabel,
    promoPrice: discountPromoPrice,
    promoTypeId: discountPromoTypeId,
  } = product.pricing.discountDetails;

  const hasDiscountPromo =
    Boolean(discountPromoCode) && Boolean(discountPromoPrice);

  // With Pricing Service V3, both bogo promoLabel (gridLabel) and
  // discount promoLabel (promoLabel) can have the "Limited Time Offer" prefix.
  // If they do, we want to remove it from gridLabel, so that it only appears once.
  //
  // Note: only Lead will have both shown at once, we don't need to remove LTO for
  // VIP users.
  const ltoPrefix = formatMessage({
    id: `grid_static.limited_time_offer`,
    defaultMessage: 'Limited Time Offer',
  });
  const discountLabelHasLtoPrefix =
    hasDiscountPromo &&
    discountPromoLabel?.length > 0 &&
    discountPromoLabel.toLowerCase().startsWith(ltoPrefix.toLowerCase());
  const bogoLabelHasLtoPrefix =
    bogoPromoLabel?.length > 0 &&
    bogoPromoLabel.toLowerCase().startsWith(ltoPrefix.toLowerCase());
  const bothHaveLtoPrefix = bogoLabelHasLtoPrefix && discountLabelHasLtoPrefix;
  const bogoLabelHasLtoColon =
    bothHaveLtoPrefix && bogoPromoLabel.charAt(ltoPrefix.length) === ':';
  // We don't show both bogo and discount labels at once for vip,
  // so no need to filter out lto prefix.
  const parsedBogoLabel =
    !isVip && bothHaveLtoPrefix
      ? bogoPromoLabel
          .slice(ltoPrefix.length + (bogoLabelHasLtoColon ? 1 : 0))
          .trim()
      : bogoPromoLabel;

  const showPromoPrice = hasDiscountPromo
    ? !promoPriceHiddenPromoTypes.has(discountPromoTypeId)
    : undefined;

  // For VIP, prioritize showing bogo promo, if not present, show discount promo.
  // For Lead, promo box will only ever show bogo promo
  let promoBoxLabel = parsedBogoLabel;
  if (isVip && !parsedBogoLabel?.length) {
    promoBoxLabel = discountPromoLabel;
  }
  const showPromoBoxLabel = promoBoxLabel && promoBoxLabel.length > 0;
  const showDiscountLabel =
    !isVip && discountPromoLabel && discountPromoLabel.length > 0;

  const price = isVip ? product.pricing.vip : product.pricing.retail;

  // Make sure to use isLoading instead of isFetching to avoid flickering on new batches of data on scroll
  // Works together with react-query's `keepPreviousData` option
  // TODO: use batch based loading state instead of blanket loading state
  if (isProductPricingLoading) {
    return <SkeletonLine width="100%" height="20px" />;
  }

  if (secondarySite) {
    const outletPrice = product.pricing.outlet;
    return (
      <PricingContainer>
        <PricingAndCredit>
          <Pricing>
            <BoldPrice amount={outletPrice} />
            <StrikeThroughPrice amount={price} />
          </Pricing>
        </PricingAndCredit>
      </PricingContainer>
    );
  }

  if (isTokenOnly) {
    return (
      <PricingContainer>
        <PricingAndCredit>
          <Pricing>
            <MemberCreditCallout product={product} isTokenOnly />
            <StrikeThroughPrice amount={price} />
          </Pricing>
        </PricingAndCredit>
      </PricingContainer>
    );
  }

  const isFreeGift = false;
  if (isFreeGift) {
    return (
      <PricingAndCredit>
        <FreeGiftContainer>
          <GiftIconContainer>
            <GiftIcon />
          </GiftIconContainer>
          <FreeGiftText>
            <FormattedMessage
              id="grid_static.free_gift"
              defaultMessage="FREE GIFT"
            />
          </FreeGiftText>
        </FreeGiftContainer>
        {showMemberCredits && <MemberCreditCallout product={product} />}
      </PricingAndCredit>
    );
  }

  return (
    <PricingContainer>
      <PricingAndCredit>
        {showDiscountLabel && <TopLabel>{discountPromoLabel}</TopLabel>}
        {showPromoPrice ? (
          <Pricing>
            <BoldPrice amount={discountPromoPrice} />
            <StrikeThroughPrice amount={price} />
          </Pricing>
        ) : (
          <Pricing>
            <BoldPrice amount={price} />
          </Pricing>
        )}
        {showMemberCredits && <MemberCreditCallout product={product} />}
      </PricingAndCredit>
      {showPromoBoxLabel && (
        <PromoBox>
          <PromoText>{promoBoxLabel}</PromoText>
        </PromoBox>
      )}
    </PricingContainer>
  );
};

ProductCardPricing.propTypes = {
  product: PropTypes.shape({
    pricing: PropTypes.object,
  }),
};

export default ProductCardPricing;
